/**
 * The palettes — the single source of every theme colour (D-2, DESIGN_SYSTEM §2).
 *
 * Abyss (dark) and Tide (light) used to be written out four times in
 * `index.css` (light, an OS-dark media query, explicit dark, explicit light),
 * with the ground colour repeated in `theme.ts`, in `index.html`'s theme-color
 * metas and in the PWA manifest. Seven copies drift. This module is the one
 * definition; everything else either imports it or is checked against it:
 *
 * - `theme.ts` and `vite.config.ts` (the manifest) **import** it.
 * - `index.css` carries the tokens as two blocks — `:root` (Tide) and
 *   `:root[data-theme="dark"]` (Abyss) — because CSS cannot import a module.
 *   `index.html`'s theme-color metas are static for the same reason (they must
 *   be right before any script runs). `tokens.test.ts` fails if either differs
 *   from this file by a single character.
 *
 * Two blocks rather than four is possible because the theme is always stamped:
 * the inline script in `index.html` and `theme.ts` write the *resolved* theme
 * to `data-theme`, so there is no state in which a media query has to decide.
 *
 * Plain data, no DOM — `vite.config.ts` imports it under Node.
 */

export type ThemeName = "light" | "dark";

/** Every `--ae-*` colour token, by the suffix after `--ae-`. */
export type ColorToken =
  | "bg"
  | "bg-2"
  | "bg-3"
  | "tx"
  | "tx-2"
  | "line"
  | "acc"
  | "acc-soft"
  | "on-acc"
  | "over"
  | "on-over"
  | "warn"
  | "water"
  | "water-over"
  | "crest"
  | "ramp-1"
  | "ramp-2"
  | "ramp-3"
  | "ramp-4"
  | "ramp-5"
  | "ramp-6"
  | "ramp-7";

/** Seven steps, as a tuple so each one is a `string` rather than `string | undefined`. */
export type Ramp = readonly [string, string, string, string, string, string, string];

/**
 * The seven-step sequential chart ramp, low to high (DESIGN_SYSTEM §2.4).
 *
 * It encodes magnitude only, so it never contains coral (D-8): "more" is
 * brighter on Abyss and darker on Tide — the ink colour of each ground.
 */
export const RAMP: Record<ThemeName, Ramp> = {
  dark: ["#0E3A40", "#145A5E", "#1C7C7C", "#27A09A", "#38C3B4", "#5FE3CF", "#B8FAEF"],
  light: ["#D4EEE9", "#95CFC4", "#5FB6A8", "#36998B", "#137D6F", "#0B5E55", "#073D38"],
};

function ramp(theme: ThemeName): Record<`ramp-${1 | 2 | 3 | 4 | 5 | 6 | 7}`, string> {
  const [r1, r2, r3, r4, r5, r6, r7] = RAMP[theme];
  return {
    "ramp-1": r1,
    "ramp-2": r2,
    "ramp-3": r3,
    "ramp-4": r4,
    "ramp-5": r5,
    "ramp-6": r6,
    "ramp-7": r7,
  };
}

export const PALETTE: Record<ThemeName, Record<ColorToken, string>> = {
  /* Tide — deep teal ink on sea foam. */
  light: {
    bg: "#F3F9F7", // ground
    "bg-2": "#E6F1EE", // recessed
    "bg-3": "#D9E9E5", // pressed
    tx: "#0E2E33", // primary ink
    "tx-2": "#4A6A6E", // secondary ink — 5.5:1 on the ground
    line: "#D5DFDE", // hairline
    acc: "#0E7C72", // accent — crest, active, primary action
    "acc-soft": "#D5E9E6", // accent wash — selection, bands
    "on-acc": "#F3F9F7", // ink on a filled accent
    over: "#C25243", // semantic: over budget, and the destructive fill (D-8)
    "on-over": "#FFF5F3", // ink on a filled `over`
    warn: "#A98419", // semantic: approaching
    water: "#4DAFA2", // the shader's body colour (DESIGN_SYSTEM §5.2)
    "water-over": "#D94D4D", // the body when flooded (D-8)
    crest: "#0E7C72", // the lit waterline
    ...ramp("light"),
  },
  /* Abyss — bioluminescent aqua on abyssal navy. */
  dark: {
    bg: "#041821",
    "bg-2": "#0A2430",
    "bg-3": "#10303F",
    tx: "#EAFBF8",
    "tx-2": "#A0B2B3", // 8.2:1 on the ground
    line: "#22363D",
    acc: "#5FF0D8",
    "acc-soft": "rgb(95 240 216 / 0.15)",
    "on-acc": "#04211D",
    over: "#FF6B6B",
    "on-over": "#2A0A0A",
    warn: "#FFB454",
    water: "#15889A",
    "water-over": "#D94D4D",
    crest: "#5FF0D8",
    ...ramp("dark"),
  },
};

/** The ground colour per theme — the status bar, the manifest, the theme-color metas. */
export const GROUND: Record<ThemeName, string> = {
  light: PALETTE.light.bg,
  dark: PALETTE.dark.bg,
};

/**
 * The PWA manifest's colours. Abyss, because the splash an installed app shows
 * before any script runs cannot know the theme, and a dark flash on a light
 * phone is gentler than a white one on a dark phone at night.
 */
export const MANIFEST_COLORS = {
  background_color: GROUND.dark,
  theme_color: GROUND.dark,
} as const;
